import { useEffect, useMemo, useState } from 'react'
import supabase from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

export default function ProjectUpdatesManager() {
  const [updates, setUpdates] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [searchTerm, setSearchTerm] = useState('')
  const [visibilityFilter, setVisibilityFilter] = useState('all')

  useEffect(() => {
    loadUpdates()
  }, [])

  async function loadUpdates() {
    try {
      setLoading(true)
      setError('')

      const { data, error: rpcError } = await supabase.rpc('codevaro_project_updates_admin_list', {
        p_project_id: null,
        p_client_id: null
      })

      if (rpcError) throw rpcError

      setUpdates(data || [])
    } catch (err) {
      console.error(err)
      setError('Σφάλμα κατά τη φόρτωση των updates.')
    } finally {
      setLoading(false)
    }
  }

  const filteredUpdates = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()

    return updates.filter((item) => {
      const matchesSearch =
        !term ||
        item.title?.toLowerCase().includes(term) ||
        item.content?.toLowerCase().includes(term) ||
        item.project_title?.toLowerCase().includes(term) ||
        item.client_name?.toLowerCase().includes(term)

      const matchesVisibility =
        visibilityFilter === 'all' ||
        (visibilityFilter === 'visible' && item.is_client_visible) ||
        (visibilityFilter === 'hidden' && !item.is_client_visible)

      return matchesSearch && matchesVisibility
    })
  }, [updates, searchTerm, visibilityFilter])

  async function toggleVisibility(item) {
    try {
      setSaving(true)
      setError('')
      setSuccess('')

      const { error: rpcError } = await supabase.rpc('codevaro_project_update_set_client_visibility', {
        p_update_id: item.update_id,
        p_is_visible: !item.is_client_visible
      })

      if (rpcError) throw rpcError

      await loadUpdates()
      setSuccess('Η ορατότητα του update ενημερώθηκε επιτυχώς.')
    } catch (err) {
      console.error(err)
      setError(`Σφάλμα κατά την ενημέρωση ορατότητας update: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  function visibilityBadgeClasses(isVisible) {
    return isVisible
      ? 'bg-green-100 text-green-800'
      : 'bg-red-100 text-red-800'
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Project Updates</h1>
        <p className="mt-1 text-sm text-gray-600">
          Διαχείριση project updates και ορατότητας στο client portal.
        </p>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      ) : null}

      {success ? (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-green-700">
          {success}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="md:col-span-2">
          <label className="mb-1 block text-sm font-medium text-gray-700">Αναζήτηση</label>
          <div className="relative">
            <SafeIcon
              icon={FiIcons.FiSearch}
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Τίτλος, περιεχόμενο, project, πελάτης..."
              className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">Portal Visibility</label>
          <select
            value={visibilityFilter}
            onChange={(e) => setVisibilityFilter(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <option value="all">Όλα</option>
            <option value="visible">Ορατά</option>
            <option value="hidden">Κρυφά</option>
          </select>
        </div>
      </div>

      <div className="rounded-xl bg-white shadow">
        {loading ? (
          <div className="flex h-48 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        ) : filteredUpdates.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <SafeIcon icon={FiIcons.FiMessageSquare} className="mx-auto mb-3 h-12 w-12 text-gray-300" />
            <p>Δεν βρέθηκαν updates.</p>
          </div>
        ) : (
          <div className="space-y-4 p-4">
            {filteredUpdates.map((item) => (
              <div
                key={item.update_id}
                className="rounded-lg border border-gray-200 p-4"
              >
                <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                  <div>
                    <div className="font-medium text-gray-900">{item.title || 'Χωρίς τίτλο'}</div>
                    <div className="mt-1 text-sm text-gray-600">
                      <div>Project: {item.project_title || '—'}</div>
                      <div>Πελάτης: {item.client_name || '—'}</div>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {item.created_at ? new Date(item.created_at).toLocaleString('el-GR') : '—'}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${visibilityBadgeClasses(item.is_client_visible)}`}>
                      {item.is_client_visible ? 'Ορατό' : 'Κρυφό'}
                    </span>

                    <button
                      onClick={() => toggleVisibility(item)}
                      disabled={saving}
                      className={`rounded-lg px-3 py-2 text-sm disabled:opacity-50 ${
                        item.is_client_visible
                          ? 'bg-red-50 text-red-700 hover:bg-red-100'
                          : 'bg-green-50 text-green-700 hover:bg-green-100'
                      }`}
                    >
                      {item.is_client_visible ? 'Απόκρυψη' : 'Εμφάνιση'}
                    </button>
                  </div>
                </div>

                <div className="whitespace-pre-wrap text-sm text-gray-700">
                  {item.content || '—'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}